/**
 * nativeFrameEnvelope.js — shared parser for the ELV1 native-live frame envelope.
 *
 * The backend media listener wraps each H.264/H.265 access unit (and audio
 * payloads) in a 32-byte "ELV1" envelope before pushing it over the
 * native-live WebSocket. NativeWebCodecsPlayer has its own (finely tuned)
 * parser for the full pipeline; this module exposes the SMALL subset the
 * JMuxer/MSE fallback needs so we don't duplicate the byte math.
 *
 * Envelope layout (big-endian), mirrored from NativeWebCodecsPlayer:
 *   [0..3]   magic "ELV1" (69,76,86,49)
 *   [4]      version
 *   [5]      channel
 *   [6]      codec (1=H264, 2=G711A, 3=H265, 4=PCM16)
 *   [7]      dataType (3 = audio)
 *   [8]      flags (bit0 = keyframe, bit1 = primer)
 *   [22..25] payloadLength (uint32)
 *   [32..]   payload bytes
 */

export const ENVELOPE_HEADER_LEN = 32;
export const NATIVE_CODEC_H264 = 1;
export const NATIVE_CODEC_H265 = 3;
const AUDIO_DATA_TYPE = 3;

const hasEnvelopeMagic = (u8) => (
  u8.length >= ENVELOPE_HEADER_LEN
  && u8[0] === 69 && u8[1] === 76 && u8[2] === 86 && u8[3] === 49
);

/**
 * Parse the ELV1 envelope header + payload.
 * @returns {{codec:number, dataType:number, isKeyframe:boolean, payload:Uint8Array, enveloped:boolean}|null}
 *          Returns null when the buffer is too short / payload bounds are invalid.
 */
export const parseEnvelope = (buffer) => {
  const u8 = new Uint8Array(buffer);
  if (!hasEnvelopeMagic(u8)) {
    // Not enveloped — treat as raw bytes (legacy listener path).
    return { codec: NATIVE_CODEC_H264, dataType: 255, isKeyframe: false, payload: u8, enveloped: false };
  }
  const view = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  const payloadLength = view.getUint32(22, false);
  const payloadStart = ENVELOPE_HEADER_LEN;
  const payloadEnd = payloadStart + payloadLength;
  if (payloadLength <= 0 || payloadEnd > u8.length) return null;
  return {
    codec: view.getUint8(6),
    dataType: view.getUint8(7),
    isKeyframe: Boolean(view.getUint8(8) & 1),
    payload: u8.subarray(payloadStart, payloadEnd),
    enveloped: true,
  };
};

/**
 * Extract an H.264 Annex-B access unit suitable for JMuxer (MSE) transmuxing.
 * Returns null for audio, HEVC, or non-video frames — JMuxer here is H.264-only.
 * @returns {Uint8Array|null}
 */
export const extractH264Annexb = (buffer) => {
  const parsed = parseEnvelope(buffer);
  if (!parsed) return null;
  if (parsed.dataType === AUDIO_DATA_TYPE) return null;
  if (parsed.codec !== NATIVE_CODEC_H264) return null;
  return parsed.payload.length > 0 ? parsed.payload : null;
};
